import { Box, Container, Typography } from '@mui/material'
import { Header } from '@tom-ui/ui'
import { PATHS, withAuthenticationRequired } from '@tom-ui/utils'
import { useTranslate } from '@tolgee/react'
import { FC } from 'react'
import { Helmet } from 'react-helmet-async'
import { Book, BookTimeline } from './BookTimeline'
import booksData from './books.json'
import { DOCS_WORD_COUNT, DOCS_WORD_COUNT_GENERATED_AT } from './docs-word-count.generated'

// Time the timeline takes to scroll to the current-docs marker on first open.
// Tweak this constant if the animation feels too fast / slow.
const INITIAL_SCROLL_DURATION_MS = 800

const books = booksData as Book[]

const DocumentationStatus: FC = withAuthenticationRequired(() => {
  const { t } = useTranslate()

  const wordCount: number | null = typeof DOCS_WORD_COUNT === 'number' ? DOCS_WORD_COUNT : null
  const generatedAt: string | null =
    typeof DOCS_WORD_COUNT_GENERATED_AT === 'string' ? DOCS_WORD_COUNT_GENERATED_AT : null

  const lastCountedLabel = generatedAt
    ? t('documentationLastCounted', 'Documentation last counted: {date}', {
        date: new Date(generatedAt).toLocaleString(),
      })
    : t('wordCountUnavailable', 'Documentation word count unavailable. Run build to generate.')

  const currentLabel = t('tomDocsLabel', 'TOM docs — {count} words', {
    count: (wordCount ?? 0).toLocaleString(),
  })

  return (
    <>
      <Helmet>
        <title>Documentation Status</title>
      </Helmet>
      <Header
        title={t('documentationStatus', 'Documentation Status')}
        breadcrumbs={[
          { label: t('profile', 'Profile'), route: PATHS.profile.url },
          { label: t('documentationStatus', 'Documentation Status') },
        ]}
      />
      <Container sx={{ paddingY: 3 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: 'background.default',
            py: 1,
            mb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant='subtitle1'>{lastCountedLabel}</Typography>
        </Box>

        {wordCount == null ? (
          <Typography variant='body2' color='text.secondary'>
            {t('wordCountUnavailable', 'Documentation word count unavailable. Run build to generate.')}
          </Typography>
        ) : (
          <BookTimeline
            books={books}
            currentWords={wordCount}
            currentLabel={currentLabel}
            initialScrollDurationMs={INITIAL_SCROLL_DURATION_MS}
          />
        )}
      </Container>
    </>
  )
})

export default DocumentationStatus
